import React, { Fragment, useEffect } from "react"
import { compose } from "redux"
import { connect } from "react-redux"
import { createStructuredSelector } from "reselect"
import PropTypes from "prop-types"

import injectSaga from "utils/injectSaga"
import injectReducer from "utils/injectReducer"

import reducer from "./reducer"
import saga from "./saga"
import {
  makeSelectDimensionsData,
  makeSelectIsLoadingDimensionsData,
} from "./selectors"
import { getDimensionsDataWatcher } from "./actions"

import "../../../Reports/components/ClimateMeasurement/index.less"

import SummaryWidgets from "../SummaryWidgets"
import WidgetAnalysisSegment from "../WidgetAnalysisSegment"
import WidgetQualitativeQuestion from "../WidgetQualitativeQuestion"
import WidgetQualitativeBubbles from "../WidgetQualitativeBubbles"
import WidgetMultipleChoiceQuestions from "../WidgetMultipleChoiceQuestions"
import SummaryQuestions from "../SummaryQuestions"
import WidgetResume from "../WidgetResume"
import WidgetResultByDimension from "../WidgetResultByDimension"
import WidgetResultByDimensionAmbassador from "../WidgetResultByDimensionAmbassador"
import WidgetResultByDivision from "../WidgetResultByDivision"

import {
  LOCAL_CHECKBOX_EXCLUDE,
  LOCAL_ISOLATED_METRICS,
  isAmbassadorActive,
} from "../../../utils/storage"
import { validate } from "../../../utils/functions"
import { get } from "lodash"
import { verifyFeatureFlag } from "../../../utils/feature-flags"

export function ClimateMeasurement(props) {
  useEffect(() => {
    const {
      idMeasure,
      getDimensionsData,
      idDivisionSelected,
      idDivisionParent,
      selectedSegment,
      measureGeneral,
    } = props

    if (!validate.exist(idMeasure) || !validate.exist(measureGeneral)) return

    const params = {
      isHome: true,
      id_measure: idMeasure,
      id_survey_type: measureGeneral.id_survey_type,
      isExclude:
        LOCAL_CHECKBOX_EXCLUDE() === "true" &&
        LOCAL_ISOLATED_METRICS() === "true",
      params: {
        id_measure: idMeasure,
        id_division: idDivisionSelected || idDivisionParent,
        id_segment: selectedSegment,
      },
    }
    if (measureGeneral.is_engagement) {
      params.dimensionType = "Engagement"
      params.is_engagement = true
    } else {
      params.is_engagement = false
    }
    getDimensionsData(params)
  }, [props.idMeasure, props.idDivisionSelected, props.selectedSegment])

  const hasSegment = props.measureGeneral.segments.length > 0

  const renderResultByDimension = () => {
    const {
      typeMeasurement,
      idMeasure,
      idDivisionSelected,
      idDivisionParent,
      measureTotal,
      measureGeneral,
      selectedSegment,
      dimensionsData,
    } = props
    if (!validate.exist(idMeasure)) return null

    if (isAmbassadorActive()) {
      return (
        <WidgetResultByDimensionAmbassador
          typeMeasurement={typeMeasurement}
          idMeasure={idMeasure}
          measureTotal={measureTotal}
          measureGeneral={measureGeneral}
          idDivisionSelected={idDivisionSelected}
          idDivisionParent={idDivisionParent}
          selectedSegment={selectedSegment}
        />
      )
    }
    return (
      <WidgetResultByDimension
        typeMeasurement={typeMeasurement}
        idMeasure={idMeasure}
        measureTotal={measureTotal}
        measureGeneral={measureGeneral}
        idDivisionSelected={idDivisionSelected}
        idDivisionParent={idDivisionParent}
        selectedSegment={selectedSegment}
        dimensionsData={dimensionsData}
      />
    )
  }

  const renderResultByDivision = () => {
    const {
      typeMeasurement,
      idMeasure,
      idDivisionSelected,
      idDivisionParent,
      measureTotal,
      measureGeneral,
      selectedSegment,
      dimensionsData,
      divisionsData,
    } = props
    if (!validate.exist(idMeasure)) return null
    return (
      <WidgetResultByDivision
        typeMeasurement={typeMeasurement}
        idMeasure={idMeasure}
        measureTotal={measureTotal}
        measureGeneral={measureGeneral}
        idDivisionSelected={idDivisionSelected}
        idDivisionParent={idDivisionParent}
        selectedSegment={selectedSegment}
        dimensionsData={dimensionsData}
        divisionsData={divisionsData}
      />
    )
  }

  const renderSegmentAnalysis = () => {
    const {
      typeMeasurement,
      idMeasure,
      idDivisionSelected,
      idDivisionParent,
      measureTotal,
      measureGeneral,
      selectedSegment,
    } = props

    if (
      measureGeneral.segments.length > 1 &&
      !verifyFeatureFlag("seg2-5") &&
      (selectedSegment === 0 || selectedSegment === null)
    ) {
      return null
    }

    if (
      measureGeneral.segments.length > 1 &&
      (selectedSegment === 0 || selectedSegment === null) &&
      measureGeneral.new_version === 0
    ) {
      return null
    }

    let newSelectedSegment = selectedSegment
    if (measureGeneral.segments.length === 1) {
      newSelectedSegment = measureGeneral.segments[0].id_segment
    }

    return (
      <WidgetAnalysisSegment
        typeMeasurement={typeMeasurement}
        idMeasure={idMeasure}
        measureTotal={measureTotal}
        measureGeneral={measureGeneral}
        idDivisionSelected={idDivisionSelected}
        idDivisionParent={idDivisionParent}
        selectedSegment={newSelectedSegment}
      />
    )
  }

  const renderQualitativeQuestions = () => {
    const {
      typeMeasurement,
      idMeasure,
      idDivisionSelected,
      idDivisionParent,
      measureTotal,
      measureGeneral,
      selectedSegment,
    } = props
    if (
      (isAmbassadorActive() &&
        !measureGeneral.ambassador_qualitative_results_visible) ||
      !validate.exist(idMeasure)
    ) {
      return null
    }
    return (
      <WidgetQualitativeQuestion
        typeMeasurement={typeMeasurement}
        idMeasure={idMeasure}
        measureTotal={measureTotal}
        measureGeneral={measureGeneral}
        idDivisionSelected={idDivisionSelected}
        idDivisionParent={idDivisionParent}
        selectedSegment={selectedSegment}
      />
    )
  }

  const renderQualitativeBubbles = () => {
    const {
      idMeasure,
      idDivisionSelected,
      idDivisionParent,
      measureTotal,
      measureGeneral,
      selectedSegment,
    } = props
    if (
      (isAmbassadorActive() &&
        !measureGeneral.ambassador_qualitative_results_visible) ||
      !validate.exist(idMeasure)
    ) {
      return null
    }
    return (
      <WidgetQualitativeBubbles
        idMeasure={idMeasure}
        measureTotal={measureTotal}
        measureGeneral={measureGeneral}
        idDivisionSelected={idDivisionSelected}
        idDivisionParent={idDivisionParent}
        selectedSegment={selectedSegment}
      />
    )
  }

  const renderMultipleChoiceQuestions = () => {
    const {
      typeMeasurement,
      idMeasure,
      idDivisionSelected,
      idDivisionParent,
      measureTotal,
      measureGeneral,
      selectedSegment,
    } = props
    if (
      measureGeneral.segments.length > 1 &&
      (selectedSegment === 0 || selectedSegment === null)
    ) {
      return null
    }
    return (
      <WidgetMultipleChoiceQuestions
        typeMeasurement={typeMeasurement}
        idMeasure={idMeasure}
        measureTotal={measureTotal}
        measureGeneral={measureGeneral}
        idDivisionSelected={idDivisionSelected}
        idDivisionParent={idDivisionParent}
        selectedSegment={selectedSegment}
      />
    )
  }

  const renderSummaryQuestions = () => {
    const {
      idMeasure,
      idDivisionSelected,
      idDivisionParent,
      measureGeneral,
      selectedSegment,
    } = props
    if (
      measureGeneral.segments.length > 1 &&
      (selectedSegment === 0 || selectedSegment === null)
    ) {
      return null
    }
    return (
      <SummaryQuestions
        idMeasure={idMeasure}
        measureGeneral={measureGeneral}
        idDivisionSelected={idDivisionSelected}
        idDivisionParent={idDivisionParent}
        selectedSegment={selectedSegment}
      />
    )
  }

  const renderEvolutionAndResult = () => {
    const {
      measureTotal,
      idDivisionSelected,
      idMeasure,
      measureGeneral,
      idDivisionParent,
      selectedSegment,
    } = props
    const isLeadership = false

    return (
      <WidgetResume
        measureGeneral={measureGeneral}
        idMeasure={idMeasure}
        idDivisionSelected={idDivisionSelected}
        idDivisionParent={idDivisionParent}
        className="WidgetResume boxShadow"
        measureData={measureTotal}
        isLeadership={isLeadership}
        selectedSegment={selectedSegment}
        title={
          <Fragment>
            <b>
              {isLeadership
                ? "Liderazgo"
                : setNameMetricsTotalLabel()}
            </b>
          </Fragment>
        }
        withEmpty
      />
    )
  }

  const setNameMetricsTotalLabel = () => {
    const { measureGeneral, selectedSegment } = props
    let response = get(measureGeneral, "survey_type_name", null)
    if (selectedSegment) {
      response = `${response} - ${get(measureGeneral, "segment_name", "")}`
    }
    return response
  }

  return (
    <div>
      {renderEvolutionAndResult()}
      {renderSummaryQuestions()}
      <SummaryWidgets
        typeMeasurement={props.typeMeasurement}
        idMeasure={props.idMeasure}
        measureTotal={props.measureTotal}
        measureGeneral={props.measureGeneral}
        idDivisionSelected={props.idDivisionSelected}
        idDivisionParent={props.idDivisionParent}
        selectedSegment={props.selectedSegment}
        hasSegment={hasSegment}
      />
      <div style={{ display: "flex" }}>
        {renderResultByDimension()}
      </div>
      {renderResultByDivision()}
      {renderSegmentAnalysis()}
      {props.measureGeneral.sentiment_analysis_version
        ? renderQualitativeBubbles()
        : renderQualitativeQuestions()}
      {renderMultipleChoiceQuestions()}
    </div>
  )
}

ClimateMeasurement.propTypes = {
  typeMeasurement: PropTypes.string,
  idMeasure: PropTypes.number,
  measureTotal: PropTypes.object,
  measureGeneral: PropTypes.object,
  idDivisionSelected: PropTypes.number,
  idDivisionParent: PropTypes.number,
  isLoading: PropTypes.bool,
  selectedSegment: PropTypes.number,
  isLoadingDimensionsData: PropTypes.bool,
  dimensionsData: PropTypes.object,
  divisionsData: PropTypes.object,
}

const mapStateToProps = createStructuredSelector({
  isLoadingDimensionsData: makeSelectIsLoadingDimensionsData(),
  dimensionsData: makeSelectDimensionsData(),
})

function mapDispatchToProps(dispatch) {
  return {
    getDimensionsData: (params) => dispatch(getDimensionsDataWatcher(params)),
  }
}

const withConnect = connect(mapStateToProps, mapDispatchToProps)
const withReducer = injectReducer({ key: "climateMeasurementV2", reducer })
const withSaga = injectSaga({ key: "climateMeasurementV2", saga })

export default compose(withReducer, withSaga, withConnect)(ClimateMeasurement)
